import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Stack,
  IconButton,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Autocomplete,
  Chip,
  CircularProgress,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { supabaseClient } from "../utils/config";
import axios from "axios";

const BRAND = "#069d6e";
const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

export default function CreateCampaign() {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");

  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState({});
  const [waitlistUsers, setWaitlistUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [customEmails, setCustomEmails] = useState([]);

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [notification, setNotification] = useState(null);

  const selectedTemplate = useMemo(
    () => templates.find((t) => String(t.id) === String(templateId)),
    [templates, templateId]
  );

  const loadData = async () => {
    setLoading(true);
    const baseUrl = process.env.REACT_APP_SERVER_URL || "";
    const api = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;

    const [templateRes, waitlistRes, groupsRes] = await Promise.all([
      supabaseClient
        .from("email_template")
        .select("id, template_name, html, template_type, created_at")
        .order("created_at", { ascending: false }),
      supabaseClient.from("waitlist").select("email, group"),
      axios.get(`${api}/groups`).catch((e) => {
        console.error("Groups load error:", e);
        return { data: { groups: [] } };
      }),
    ]);

    if (templateRes.error) console.error("Templates load error:", templateRes.error);
    setTemplates(templateRes.data || []);

    if (waitlistRes.error) console.error("Waitlist load error:", waitlistRes.error);
    const rows = waitlistRes.data || [];

    const groups = (groupsRes?.data?.groups || []).map((g) => g.name);
    setAvailableGroups(groups);
    const initial = {};
    groups.forEach((g) => (initial[g] = false));
    setSelectedGroups(initial);

    const emails = [
      ...new Set(rows.map((r) => r.email).filter((e) => e && e.trim() !== "")),
    ];
    setWaitlistUsers(emails);

    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const toggleGroup = (name) =>
    setSelectedGroups((prev) => ({ ...prev, [name]: !prev[name] }));

  const selectedGroupNames = Object.keys(selectedGroups).filter(
    (g) => selectedGroups[g]
  );

  const resetForm = () => {
    setSubject("");
    setTemplateId("");
    setSelectedUsers([]);
    setCustomEmails([]);
    setSelectedGroups((prev) => {
      const reset = {};
      Object.keys(prev).forEach((g) => (reset[g] = false));
      return reset;
    });
  };

  const submitCampaign = async (send) => {
    if (!templateId || !selectedTemplate?.html) {
      setNotification({ type: "error", msg: "Please select a template." });
      return;
    }
    if (!subject.trim()) {
      setNotification({ type: "error", msg: "Please enter a subject." });
      return;
    }

    const invalidCustom = customEmails.filter((e) => !emailRegex.test(e));
    if (invalidCustom.length > 0) {
      setNotification({
        type: "error",
        msg: `Invalid custom email(s): ${invalidCustom.join(", ")}`,
      });
      return;
    }

    if (
      selectedGroupNames.length === 0 &&
      selectedUsers.length === 0 &&
      customEmails.length === 0
    ) {
      setNotification({
        type: "error",
        msg: "Select at least one group, specific user, or custom email.",
      });
      return;
    }

    const payload = {
      subject: subject.trim(),
      templateId: selectedTemplate.id,
      templateName: selectedTemplate.template_name,
      templateHtml: selectedTemplate.html,
      selectedGroups: selectedGroupNames,
      selectedEmails: selectedUsers,
      otherEmails: customEmails,
      includedWaitlist: false,
      send,
    };

    const baseUrl = process.env.REACT_APP_SERVER_URL || "";
    const apiUrl = baseUrl.endsWith("/")
      ? `${baseUrl}email/campaigns`
      : `${baseUrl}/email/campaigns`;

    setSending(true);
    try {
      const { data } = await axios.post(apiUrl, payload);
      setNotification({
        type: "success",
        msg: send
          ? `Campaign created and sent to ${data?.totalSent ?? "the selected"} recipients.`
          : "Campaign created. You can send it anytime from the Campaigns page.",
      });
      resetForm();
    } catch (error) {
      console.error("Campaign error:", error?.response?.data || error);
      setNotification({
        type: "error",
        msg: `Failed: ${error?.response?.data?.message || error.message}`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f7f9]">
      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setNotification(null)}
      >
        {notification ? (
          <Alert
            severity={notification.type}
            variant="filled"
            onClose={() => setNotification(null)}
            sx={{ width: "100%" }}
            action={
              notification.type === "success" ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate("/campaigns")}
                >
                  View Campaigns
                </Button>
              ) : null
            }
          >
            {notification.msg}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>

      <div className="flex items-center gap-3 p-6">
        <IconButton onClick={() => navigate("/")}>
          <ArrowBackIcon />
        </IconButton>
        <div className="text-black font-semibold text-lg">Create Campaign</div>
      </div>

      {loading ? (
        <div className="flex justify-center mt-16">
          <CircularProgress style={{ color: BRAND }} />
        </div>
      ) : (
        <Box
          sx={{
            width: "70%",
            maxWidth: 900,
            margin: "auto",
            backgroundColor: "#fff",
            borderRadius: 3,
            p: 4,
            mb: 6,
          }}
        >
          <Stack spacing={4}>
            <div>
              <div className="text-black font-medium mb-2">1. Template</div>
              <TextField
                select
                fullWidth
                label="Select a template"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
              >
                {templates.length === 0 && (
                  <MenuItem disabled value="">
                    No templates found
                  </MenuItem>
                )}
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.template_name || `Untitled (${t.template_type})`}
                  </MenuItem>
                ))}
              </TextField>

              {selectedTemplate?.html && (
                <Box
                  sx={{
                    mt: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <div className="text-xs text-gray-500 px-3 py-1 bg-[#f5f7f9]">
                    Preview
                  </div>
                  <iframe
                    title="template-preview"
                    srcDoc={selectedTemplate.html}
                    style={{ width: "100%", height: 320, border: "none" }}
                  />
                </Box>
              )}
            </div>

            <Divider />

            <div>
              <div className="text-black font-medium mb-2">2. Subject</div>
              <TextField
                fullWidth
                label="Email subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
              />
            </div>

            <Divider />

            <div>
              <div className="text-black font-medium mb-2">3. Recipients</div>

              {availableGroups.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <div className="text-sm text-gray-600 mb-1">Groups</div>
                  <Stack direction="row" flexWrap="wrap">
                    {availableGroups.map((g) => (
                      <FormControlLabel
                        key={g}
                        className="capitalize"
                        control={
                          <Checkbox
                            checked={selectedGroups[g] || false}
                            onChange={() => toggleGroup(g)}
                            sx={{ "&.Mui-checked": { color: BRAND } }}
                          />
                        }
                        style={{ color: "black" }}
                        label={g}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <div className="text-sm text-gray-600 mb-1">
                  Specific users (from waitlist)
                </div>
                <Autocomplete
                  multiple
                  options={waitlistUsers}
                  value={selectedUsers}
                  onChange={(_e, val) => setSelectedUsers(val)}
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search and select waitlist users"
                    />
                  )}
                />
              </Box>

              <Box>
                <div className="text-sm text-gray-600 mb-1">
                  Custom emails (optional)
                </div>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={customEmails}
                  onChange={(_e, val) => setCustomEmails(val)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        color={emailRegex.test(option) ? "default" : "error"}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Type an email and press Enter"
                    />
                  )}
                />
              </Box>
            </div>

            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              flexWrap="wrap"
            >
              <Button
                onClick={() => submitCampaign(false)}
                disabled={sending}
                variant="outlined"
                sx={{
                  borderColor: BRAND,
                  color: BRAND,
                  width: 200,
                  height: 50,
                  "&:hover": { borderColor: BRAND },
                }}
              >
                Create Campaign
              </Button>
              <Button
                onClick={() => submitCampaign(true)}
                disabled={sending}
                sx={{
                  backgroundColor: BRAND,
                  color: "white",
                  width: 200,
                  height: 50,
                  "&:hover": { backgroundColor: BRAND, color: "white" },
                  "&.Mui-disabled": { backgroundColor: "#9ccabb", color: "#fff" },
                }}
              >
                {sending ? (
                  <CircularProgress size={24} sx={{ color: "#fff" }} />
                ) : (
                  "Send Campaign"
                )}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
    </div>
  );
}
